import { useEffect, useRef } from 'react';

import styles from './ProductPreview.module.css';

/**
 * "Produto vivo": the app's own "No seu pedido" card, slightly tilted, buying and delivering by
 * itself — a ghost cursor taps "+" on two products (each becomes the pill), taps "Finalizar", and
 * the truck in the same card runs to "Entregue". One clock, one loop of 15 s, so the three steps of
 * the first-visit tour each have their own beat (`onStep`). Decorative (`aria-hidden`); stops while
 * the tab is hidden; reduced motion — or no way to measure, as in tests — shows the delivered card.
 */

const ROWS = [
  {
    key: 'arroz',
    name: 'Arroz agulhinha tipo 1',
    code: 'ARR-5KG',
    price: 6.14,
    unit: 'kg',
    qty: 10,
    tone: 1,
    start: true,
  },
  {
    key: 'tomate',
    name: 'Tomate italiano',
    code: 'TOM-ITA',
    price: 6.99,
    unit: 'kg',
    qty: 6,
    tone: 5,
    start: false,
  },
  {
    key: 'leite',
    name: 'Leite integral',
    code: 'LEI-INT',
    price: 4.29,
    unit: 'L',
    qty: 12,
    tone: 4,
    start: false,
  },
] as const;

const ICONS: Readonly<Record<number, string>> = {
  1: 'M8 3h8l-1 3H9zM9 6c-3 2-4 5-4 8 0 4 3 7 7 7s7-3 7-7c0-3-1-6-4-8M10 13h4',
  4: 'M8 7l2-4h4l2 4v14H8zM8 7h8M11 12h2',
  5: 'M12 8c-4-2-8 1-7 6 1 4 4 7 7 6 3 1 6-2 7-6 1-5-3-8-7-6zM12 8c0-2 1-4 3-5',
};

const LOOP_MS = 15_000;
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** [start, end, action, row] in loop fractions. */
const PLAN = [
  [0.04, 0.11, 'move', 1],
  [0.12, 0.13, 'click', 1],
  [0.17, 0.24, 'move', 2],
  [0.25, 0.26, 'click', 2],
  [0.3, 0.37, 'moveFinish', 0],
  [0.38, 0.39, 'clickFinish', 0],
  [0.42, 0.48, 'leave', 0],
] as const;
const RUN_START = 0.47;
const RUN_END = 0.86;

const clamp = (v: number) => Math.min(1, Math.max(0, v));
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export type ProductPreviewStep = 1 | 2 | 3;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

function animate(root: HTMLElement, onStep: (step: ProductPreviewStep) => void): () => void {
  const cursor = root.querySelector<HTMLElement>('[data-pv="cursor"]');
  const chip = root.querySelector<HTMLElement>('[data-pv="chip"]');
  const count = root.querySelector<HTMLElement>('[data-pv="count"]');
  const sum = root.querySelector<HTMLElement>('[data-pv="sum"]');
  const fill = root.querySelector<HTMLElement>('[data-pv="fill"]');
  const truck = root.querySelector<HTMLElement>('[data-pv="truck"]');
  const rows = [...root.querySelectorAll<HTMLElement>('[data-pv="row"]')];
  const labels = [...root.querySelectorAll<HTMLElement>('[data-pv="label"]')];
  if (!cursor || !chip || !count || !sum || !fill || !truck) {
    return () => undefined;
  }

  const inOrder = ROWS.map((row) => row.start);
  let step: ProductPreviewStep | 0 = 0;
  let chipText = '';
  const setStep = (next: ProductPreviewStep) => {
    if (next !== step) {
      step = next;
      onStep(next);
    }
  };
  const setChip = (text: string, tone: 'finish' | 'go' | 'idle') => {
    if (text !== chipText) {
      chipText = text;
      chip.textContent = text;
    }
    chip.dataset.tone = tone;
  };
  const paintOrder = () => {
    rows.forEach((row, index) => row.toggleAttribute('data-in', inOrder[index] === true));
    const n = inOrder.filter(Boolean).length;
    count.textContent = `${String(n)} ${n === 1 ? 'item' : 'itens'}`;
    sum.textContent = BRL.format(
      ROWS.reduce((total, row, index) => total + (inOrder[index] ? row.qty * row.price : 0), 0),
    );
  };
  const setTrack = (p: number) => {
    fill.style.transform = `scaleX(${String(p)})`;
    truck.style.insetInlineStart = `${String(p * 100)}%`;
    labels.forEach((label, index) =>
      label.toggleAttribute('data-on', p >= [0.001, 0.33, 0.66, 0.999][index]!),
    );
  };
  const reset = () => {
    ROWS.forEach((row, index) => {
      inOrder[index] = row.start;
    });
    paintOrder();
    setTrack(0);
    setChip('Finalizar', 'finish');
    setStep(1);
  };

  if (prefersReducedMotion() || typeof root.getBoundingClientRect !== 'function') {
    ROWS.forEach((_, index) => {
      inOrder[index] = true;
    });
    paintOrder();
    setTrack(1);
    setChip('Entregue', 'go');
    setStep(3);
    return () => undefined;
  }

  const centre = (el: Element) => {
    const box = root.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    return {
      x: rect.left - box.left + rect.width * 0.55,
      y: rect.top - box.top + rect.height * 0.6,
    };
  };
  let from = { x: 0, y: 0 };
  let to = { x: 0, y: 0 };
  let target: HTMLElement | null = null;
  let phase = 0;
  let cycle = -1;
  let frame = 0;
  let elapsed = 0;
  let last = performance.now();

  const tick = (now: number) => {
    elapsed += now - last;
    last = now;
    const thisCycle = Math.floor(elapsed / LOOP_MS);
    const c = (elapsed % LOOP_MS) / LOOP_MS;

    if (thisCycle !== cycle) {
      cycle = thisCycle;
      phase = 0;
      target = null;
      reset();
      const box = root.getBoundingClientRect();
      from = to = { x: box.width - 30, y: box.height - 10 };
      cursor.removeAttribute('data-show');
    }

    PLAN.forEach(([start, , action, row], index) => {
      if (c < start || phase >= index + 1) {
        return;
      }
      phase = index + 1;
      if (action === 'move' || action === 'moveFinish') {
        const next =
          action === 'move' ? rows[row]?.querySelector<HTMLElement>('[data-pv="add"]') : chip;
        from = { ...to };
        if (next) {
          to = centre(next);
        }
        target = next ?? null;
        cursor.setAttribute('data-show', '');
        if (action === 'moveFinish') {
          setStep(2);
        }
      } else if (action === 'click' || action === 'clickFinish') {
        target?.setAttribute('data-press', '');
        const pressed = target;
        window.setTimeout(() => {
          pressed?.removeAttribute('data-press');
          if (action === 'click') {
            inOrder[row] = true;
            paintOrder();
          } else {
            setChip('Enviado', 'go');
          }
        }, 140);
      } else {
        target?.removeAttribute('data-hover');
        from = { ...to };
        const box = root.getBoundingClientRect();
        to = { x: box.width + 12, y: box.height - 50 };
        target = null;
      }
    });

    const moving = PLAN.find(
      ([start, end, action]) =>
        action !== 'click' && action !== 'clickFinish' && c >= start && c <= end,
    );
    if (moving) {
      const e = easeInOut(clamp((c - moving[0]) / (moving[1] - moving[0])));
      cursor.style.transform = `translate(${String(from.x + (to.x - from.x) * e)}px, ${String(from.y + (to.y - from.y) * e)}px)`;
      target?.toggleAttribute('data-hover', e > 0.85);
    }
    if (c > 0.47) {
      cursor.removeAttribute('data-show');
    }

    if (c >= 0.39 && c < RUN_END) {
      setChip(c < RUN_START + 0.06 ? 'Enviado' : 'A caminho', 'go');
      setTrack(easeInOut(clamp((c - RUN_START) / (RUN_END - RUN_START))));
      if (c >= RUN_START) {
        setStep(3);
      }
    } else if (c >= RUN_END) {
      setChip('Entregue', 'go');
      setTrack(1);
    }
    frame = requestAnimationFrame(tick);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      cancelAnimationFrame(frame);
    } else {
      last = performance.now();
      frame = requestAnimationFrame(tick);
    }
  };

  frame = requestAnimationFrame(tick);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    cancelAnimationFrame(frame);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export interface ProductPreviewProps {
  readonly onStep?: ((step: ProductPreviewStep) => void) | undefined;
  readonly className?: string | undefined;
}

export function ProductPreview({ onStep, className }: ProductPreviewProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stepRef = useRef(onStep);

  useEffect(() => {
    stepRef.current = onStep;
  }, [onStep]);

  useEffect(() => {
    const root = rootRef.current;
    if (root === null) {
      return undefined;
    }
    return animate(root, (step) => {
      stepRef.current?.(step);
    });
  }, []);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      ref={rootRef}
      aria-hidden="true"
    >
      <div className={styles.stage}>
        <div className={styles.card}>
          <div className={styles.head}>
            <span className={styles.title}>No seu pedido</span>
            <span className={styles.count} data-pv="count">
              1 item
            </span>
            <span className={styles.chip} data-pv="chip" data-tone="finish">
              Finalizar
            </span>
            <span className={styles.sum} data-pv="sum" />
          </div>
          <div className={styles.foot}>
            <div className={styles.track}>
              <span className={styles.fill} data-pv="fill" />
              <span className={styles.truck} data-pv="truck">
                <svg viewBox="0 0 30 22" width="22" height="16">
                  <path d="M1 3h17v12H1zM18 7h6l4 4v4H18z" />
                  <circle cx="7" cy="17" r="2.5" />
                  <circle cx="22" cy="17" r="2.5" />
                </svg>
              </span>
            </div>
            <div className={styles.labels}>
              {['Enviado', 'Separado', 'A caminho', 'Entregue'].map((label) => (
                <span key={label} data-pv="label">
                  {label}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.list}>
            {ROWS.map((row) => (
              <div key={row.key} className={styles.row} data-pv="row">
                <span
                  className={styles.tile}
                  style={{
                    background: `var(--color-cat-${String(row.tone)}-bg)`,
                    color: `var(--color-cat-${String(row.tone)})`,
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={ICONS[row.tone]} />
                  </svg>
                </span>
                <span className={styles.identity}>
                  <span className={styles.name}>{row.name}</span>
                  <span className={styles.code}>
                    {row.code} · {BRL.format(row.price)} / {row.unit}
                  </span>
                </span>
                <span className={styles.control}>
                  <span className={styles.add} data-pv="add">
                    +
                  </span>
                  <span className={styles.pill}>
                    <span>−</span>
                    <b>{row.qty}</b>
                    <small>{row.unit}</small>
                    <span>+</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <svg className={styles.cursor} data-pv="cursor" viewBox="0 0 18 22">
        <path d="M2 1l13 11-6 1 3.5 7-2.6 1.2-3.4-7L2 18z" />
      </svg>
    </div>
  );
}
